import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as DownloadDownIcon } from "../../Assets/Icons/DownloadDownIcon.svg";
import { ReactComponent as CrossSolid } from "../../Assets/Icons/CrossSolid.svg";
import { ReactComponent as HospitalComparisonTitle } from "../../Assets/Icons/HospitalComparisonTitle.svg";
import {
  CompareIconWrapper,
  CrossWrapper,
  MainContainer,
  TitleContainer,
  TopSection,
  Wrapper,
  FilterBox,
  Center,
  Title,
  ClearAll,
} from "./index.styled";
import HospitalFilterMenu from "../HospitalFilterMenu/index.jsx";
import ComparisonTable from "../HospitalComparisonTable/index.jsx";
import { getCompareHospitals, getAllHospitals } from "../../services/apiService";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import exportFromJSON from "export-from-json";
import * as XLSX from "xlsx-js-style";
import OptionsList from "../OptionsList/index.js";
import {
  exportOptions,
  HospitalsOptions as options,
  views,
} from "../../Configs/config";
import { Loader } from "../../App.js";
import {
  formatLocation,
  toTitleCase,
  viewSizeCalculator,
} from "../../utils/index.js";
import { useDispatch, useSelector } from "react-redux";
import Info from "../Info/index.jsx";
import {
  clearProviderNumbers,
  setHospitalsDetails,
} from "../../Redux/app/app.actions.js";
import { resetSelectedState, resetSpecificStateData } from "../../Redux/hospital/hospital.actions";
import { useFilters } from "../../hooks/useFilters";
import { clearHospitalsCompare, removeEmptyHospitals } from "../../Redux/compare/compare.actions.js"
import NoHospital from "../NoHospital/index.jsx";
import {
  addCommasToNumber,
  formatAreaSmart,
  formatNumber,
  formatPercentage,
} from "../FactSheetLeft/dataHandler.js";
import { useSnack } from "../SnackBar";

const HospitalComparison = ({
  toggleState,
}) => {
  const { compareHospitals } = useSelector((state) => state.compare);
  const compareHospitalsCount = (compareHospitals.filter(h => Number(h.provider_number))).length;
  // Once the compare shell has rendered, dropdown hospital changes must use
  // column loaders only — never the full-page spinner again for this visit.
  const [hasShownCompareShell, setHasShownCompareShell] = useState(false);

  const providerNumbersList = (compareHospitals || [])
    .map((hospital) => hospital.provider_number)
    .filter(Boolean);
  const compareKey = providerNumbersList.slice().sort().join(",");

  // Comparison data is cached per selected-provider set. keepPreviousData keeps
  // already-loaded columns visible while a newly added/replaced hospital
  // fetches, so only that column shows a skeleton.
  const { data: compareResponse, isLoading: compareLoading } = useQuery({
    queryKey: ["compare-hospitals", compareKey],
    queryFn: ({ signal }) => getCompareHospitals(providerNumbersList, signal),
    enabled: providerNumbersList.length > 0,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  // One column per selected slot, in slot order. A slot whose data is not in
  // the current response yet becomes a pending column (skeleton). Deriving the
  // loading flag from the data means it can never get "stuck" and a last-slot
  // add gets its own column immediately.
  const data = useMemo(() => {
    const byProvider = new Map(
      (Array.isArray(compareResponse) ? compareResponse : []).map((h) => [
        String(h.provider_number),
        h,
      ]),
    );
    return compareHospitals
      .filter((h) => h?.provider_number)
      .map(
        (h) =>
          byProvider.get(String(h.provider_number)) ?? {
            provider_number: h.provider_number,
            __pending: true,
          },
      );
  }, [compareResponse, compareHospitals]);

  const tableLoading = useMemo(
    () => data.map((h) => h.__pending === true),
    [data],
  );

  // Full-page loader: only on first entry into compare (navigate-in with
  // hospitals already selected). Never for later dropdown add/replace.
  const showPageLoader =
    !hasShownCompareShell && providerNumbersList.length > 0 && compareLoading;

  useEffect(() => {
    if (!showPageLoader) {
      setHasShownCompareShell(true);
    }
  }, [showPageLoader]);

  // Full hospitals list for the comparison dropdown (near-static, cached long).
  const { data: allHospitalsResponse } = useQuery({
    queryKey: ["hospitals", { endpoint: "all", page: 1, perPage: 1000 }],
    queryFn: ({ signal }) => getAllHospitals({ page: 1, perPage: 1000 }, signal),
    staleTime: 30 * 60 * 1000,
  });
  const [exportOption, setExportOption] = useState(null);
  const [preventToggle, setPreventToggle] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(
    searchResults.length != 0 ? true : false,
  );

  const dispatch = useDispatch();
  const snack = useSnack();
  const { resetFilters } = useFilters();

  const headerScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [showGradient, setShowGradient] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    dispatch(removeEmptyHospitals());
  
    return () => {
      dispatch(removeEmptyHospitals());
    };
  }, []);

  const checkIfScrolledToEnd = () => {
    if (!headerScrollRef.current || !tableScrollRef.current) return;

    const {
      scrollLeft: headerLeft,
      scrollWidth: headerWidth,
      clientWidth: headerClient,
    } = headerScrollRef.current;
    const {
      scrollLeft: tableLeft,
      scrollWidth: tableWidth,
      clientWidth: tableClient,
    } = tableScrollRef.current;

    const isAtEnd =
      headerLeft + headerClient >= headerWidth - 10 &&
      tableLeft + tableClient >= tableWidth - 20;

    // setShowGradient(!isAtEnd);
  };

  const handleScroll = (source) => {
    if (source === "header" && tableScrollRef.current) {
      tableScrollRef.current.scrollLeft = headerScrollRef.current.scrollLeft;
    } else if (source === "table" && headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
    checkIfScrolledToEnd();
  };

  const toggleDropdown = () => {
    if (compareHospitals.length === 0) return;
    if (preventToggle) setPreventToggle(false);
    setIsOpen(!isOpen);
  };

  const onPreventToggle = (flag) => {
    setPreventToggle(flag);
  };

  const handleOptionClick = (option) => {
    setExportOption(option);
    setIsOpen(false);
    onExportLocal(option.type);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setPreventToggle(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const transformHospitalDataToOptions = (hospitalData) =>
    hospitalData
          .filter(({ hosp10_name }) => hosp10_name)
    .map(({ hosp10_name, county, city, state, provider_number, esa }) => ({
      value: hosp10_name,
      label: toTitleCase(hosp10_name),
        location: formatLocation(`${city}, ${state}`),
        provider_number,
        esa,
      }));

  useEffect(() => {
    dispatch(resetSelectedState());
    dispatch(resetSpecificStateData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Populate the comparison dropdown from the cached all-hospitals list.
  useEffect(() => {
    if (!allHospitalsResponse) return;
    const hospitalData = allHospitalsResponse.data || allHospitalsResponse;
    setDropdownOptions(transformHospitalDataToOptions(hospitalData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allHospitalsResponse]);

  const formatTeachingStatus = (status) => {
    if (status == null || status === "-" || status === "") return "-";
    return ["1", 1, "Y", "Yes", true].includes(status) ? "Yes" : "No";
  };

  const formatQualityValue = (value) => {
    if (
      value == null ||
      value === "-" ||
      value === "" ||
      value === "None" ||
      value === "None%"
    )
      return "N/A";
    return value;
  };

  const emptyToDash = (value) => {
    if (value == null || value === "" || value === "-") return "-";
    return value;
  };

  const onExportLocal = (type) => {
    const fileName = "Compare-Hospitals";
    const exportType = exportFromJSON.types[type];

    const filteredData = data.filter(
      (item) => item.provider_number && item.provider_number !== "undefined",
    );

    const formattedData = filteredData.map((item) => {
      return {
        Name: item?.name ?? "-",
        Location: item?.location ?? "-",
        Contact: item.facilities?.contact ?? "-",

        // Facilities
        Ownership: item.facilities?.ownership ?? "-",
        "Facility Type": item.facilities?.facility_type ?? "-",
        Geography: item.facilities?.geography ?? "-",
        "Hospital Beds":
          addCommasToNumber(item.facilities?.hospital_beds) ?? "-",
        "Patient Volume": item.facilities?.patient_volume ?? "-",
        "Hospital Area": formatAreaSmart(item.facilities?.hospital_area) ?? "-",
        Discharges:
          item.facilities?.discharges != null
            ? addCommasToNumber(item.facilities.discharges.toFixed(0))
            : "-",
        Admissions: addCommasToNumber(item.facilities?.admissions) ?? "-",
        "Teaching Status": formatTeachingStatus(
          item.facilities?.teaching_status,
        ),
        "NPI Number": item.facilities?.npi_number ?? "-",
        "Firm Type": item.facilities?.firm_type ?? "-",
        "Number of Employees":
          addCommasToNumber(item.facilities?.number_employees) ?? "-",
        "Number of Operating Rooms":
          addCommasToNumber(item.facilities?.num_operating_rooms) ?? "-",
        "340B ID": item.facilities?.program_340b_id ?? "-",
        "340B ID Classification":
          item.facilities?.hospital_340b_id_classification ?? "-",
        "Number of Affiliated Physicians":
          addCommasToNumber(item.facilities?.number_affiliated_physicians) ??
          "-",
        Website: item.facilities?.website ?? "-",

        // Financials
        "Gross Revenue": formatNumber(item.financials?.gross_revenue),
        "Net Revenue": formatNumber(item.financials?.net_revenue),
        "Net Patient Revenue": formatNumber(
          item.financials?.net_patient_revenue ?? "-",
        ),
        "Operating Income": formatNumber(
          item.financials?.operating_income ?? "-",
        ),
        "Total Operating Expenses": formatNumber(
          item.financials?.tot_operating_expenses ?? "-",
        ),
        "Net Income": formatNumber(item.financials?.net_income ?? "-"),
        "Net Income Margin":
          item.financials?.net_income_margin &&
          item.financials?.net_income_margin[0] !== "-"
            ? `${item.financials?.net_income_margin}%`
            : "-",
        "Outpatient Revenue": formatNumber(
          item.financials?.outpatient_revenue ?? "-",
        ),
        "Net Revenue Oncology": formatNumber(
          item.financials?.net_revenue_oncology ?? "-",
        ),
        "Revenue to Cost Ratio": item.financials?.revenue_to_cost_ratio ?? "-",
        "Bad Debt Percentage": formatPercentage(
          item.financials?.bad_debt_percentage,
        ),

        // Affiliations
        "Medical School Affiliation": item.medical_school_affiliation ?? "-",

        // Accreditations — a hospital can have multiple; the API returns them
        // as a single comma-separated string, so normalize and join cleanly.
        Accreditations: (() => {
          const raw = item.accreditations;
          if (!raw || raw === "-") return "-";
          const list = String(raw)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          return list.length > 0 ? list.join(", ") : "-";
        })(),
  
        // Oncology Focus
        "Radiation Therapy": addCommasToNumber(item.oncology_focus?.radiation_therapy) ?? "-",
        "Chemo Therapy": addCommasToNumber(item.oncology_focus?.chemo_therapy) ?? "-",
        "Surgical Oncology": addCommasToNumber(item.oncology_focus?.procedures) ?? "-",

        // Quality of Care
        NCCN: item.quality_of_care?.nccn ? "Yes" : "No",
        C4QI: item.quality_of_care?.c4qi ? "Yes" : "No",
        "Oncology Mortality": formatQualityValue(
          item.quality_of_care?.oncology_mortality,
        ),
        "Readmissions Rate": formatQualityValue(
          item.quality_of_care?.readmissions_rate,
        ),
        HCAHPS: formatQualityValue(item.quality_of_care?.hcahps),

        // Accessibility & Inclusivity
        "Geographic Accessibility": item.accessibility_inclusivity?.geographic_accessibility ?? "-",

        // Patient Experience
        "Communication With Nurses/Doctors":
          emptyToDash(item.patient_experience?.communication_with_nurses_doctors),
        "Appointment Wait Times":
          emptyToDash(item.patient_experience?.appointment_wait_times),
        "Cleanliness and Quietness":
          emptyToDash(item.patient_experience?.cleanliness_and_quietness),
        "Likelihood of Recommendation":
          emptyToDash(item.patient_experience?.likelihood_of_recommendation),
        "Cancer Navigation Program": item.patient_experience
          ?.cancer_navigation_program
          ? "Yes"
          : "No",
      };
    });

    if (type === "json") {
      exportFromJSON({ data: formattedData, fileName, exportType: "json" });
      return;
    }

    const keys = Object.keys(formattedData[0]);
    const transposedData = keys.map((key) => ({
      Attribute: key,
      ...Object.fromEntries(
        formattedData.map((item, index) => [
          `Hospital ${index + 1}`,
          item[key] ?? "-",
        ]),
      ),
    }));

    if (type === "xlsx") {
      const worksheet = XLSX.utils.json_to_sheet(transposedData);

      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
        if (worksheet[cellRef]) {
          worksheet[cellRef].s = { font: { bold: true } };
        }
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Comparison");
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
      return;
    }

    exportFromJSON({ data: transposedData, fileName, exportType });
  };

  const handleClose = () => {
    dispatch(setHospitalsDetails({ tableTitle: "" }));
    dispatch(clearProviderNumbers());
    dispatch(resetSelectedState());
    dispatch(resetSpecificStateData());
    // Exit compare back to the map AND clear advanced filters + map/legend
    // selection in a SINGLE URL write. Doing both in one navigation avoids the
    // race that previously let a stale write snap the view back to compare.
    resetFilters(views.MAP);
  };

  const handleClearAll = () => {
    snack(
      "Removed All Hospitals from Compare Screen!",
      "#FFC640",
      "#333333"
    );
    dispatch(clearHospitalsCompare());
  };

  const searchParams = {
    searchTerm,
    searchResults,
    setSearchResults,
    setSearchTerm,
    isSearchOpen,
    setIsSearchOpen,
  };

  if (showPageLoader)
    return (
      <Center>
        <Loader height={viewSizeCalculator(3)} width={viewSizeCalculator(3)} />
      </Center>
    );

  return (
    <MainContainer>
      <TopSection>
        <TitleContainer>
          <CompareIconWrapper>
            <HospitalComparisonTitle style={{ color: "red" }} />
          </CompareIconWrapper>
          <Title>Compare Hospitals</Title>
        </TitleContainer>
        <Wrapper>
          {compareHospitalsCount !== 0 && 
            <Info
              showArrow={false}
              config={{
                tooltipText: "Remove all Hospitals",
                position: "top",
              }}
            >
              <ClearAll onClick={handleClearAll}>Clear All</ClearAll>
            </Info>
          }
          <FilterBox
            onClick={toggleDropdown}
            ref={dropdownRef}
            isDisabled={compareHospitalsCount === 0}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            isHovered={isHovered}
          >
            <Info
              showArrow={false}
              config={{
                tooltipText: "Download",
                position: "top",
              }}
            >
              <DownloadDownIcon
                style={{
                  stroke: isHovered ? "#F96C50" : "#333333",
                }}
              />
            </Info>
            <OptionsList
              options={exportOptions}
              onOptionClick={handleOptionClick}
              selectedOption={exportOptions}
              customWidth={15.75}
              top={3}
              showIcon={false}
              right={true}
              onPreventToggle={onPreventToggle}
              showTitle={"Export"}
              customPadding={0.313}
              customBorderRadius={0.625}
              isOpen={isOpen}
            />
          </FilterBox>
          <Info
            showArrow={false}
            config={{
              tooltipText: "Close",
              position: "top",
            }}
          >
            <CrossWrapper onClick={handleClose}>
              <CrossSolid />
            </CrossWrapper>
          </Info>
        </Wrapper>
      </TopSection>
      {/* {compareHospitals && compareHospitals.length == 0 && (
        <NoHospital toggleState={toggleState} />
      )} */}

      {data && (
        <HospitalFilterMenu
          hospitalsData={data}
          searchParams={searchParams}
          options={dropdownOptions}
          scrollRef={headerScrollRef}
          onScroll={() => handleScroll("header")}
          showGradient={showGradient}
        />
      )}

      {data && (
        <ComparisonTable
          hospitalsData={compareHospitals.length === 0 ? [] : data}
          searchResults={searchResults}
          isSearchOpen={isSearchOpen}
          setIsSearchOpen={setIsSearchOpen}
          tableLoading={tableLoading}
          scrollRef={tableScrollRef}
          onScroll={() => handleScroll("table")}
          showGradient={showGradient}
        />
      )}
    </MainContainer>
  );
};

export default HospitalComparison;
